import { useEffect, useRef, useState } from 'preact/hooks';
import type { ActionId, MatchStart } from '@cratered/shared';
import { MatchSession, type HudState } from '../game/match';
import { lookFor } from '../game/look';
import { sfx } from '../game/audio';
import { damageBoat } from '../game/boats';
import { TEAM_COLORS } from '../game/render';
import { ACTIONS } from '../game/weapons';
import { layoutMode, lobby, net, profile, self } from '../store';
import { ActionIcon, Avatar, MoleCanvas, TeamDot } from './common';
import { PadInput } from './gamepad';

interface Callout {
  id: number;
  text: string;
}

const TURN_PHASES = new Set(['select', 'move', 'aim']);

/**
 * The inventory grid's geometry, matching .inventory in styles.css: 7 tiles of 42 px, 4 px apart,
 * inside 6 px of padding and a 1 px border. The HUD needs the numbers to keep the whole grid on
 * screen, since it hangs above the active mole, who may be standing at either edge.
 */
const INV_COLS = 7;
const INV_TILE = 42;
const INV_GAP = 4;
const INV_EDGE = 7;
/** Breathing room between the grid and the edge of the battlefield. */
const INV_MARGIN = 6;

export function Match({ start }: { start: MatchStart }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const sessionRef = useRef<MatchSession | null>(null);
  const [hud, setHud] = useState<HudState | null>(null);
  const [callouts, setCallouts] = useState<Callout[]>([]);
  const [confirmForfeit, setConfirmForfeit] = useState(false);
  const lastHudSig = useRef('');
  const myId = self.value?.id ?? '';
  const amPlayer = start.players.some((p) => p.id === myId);
  const pip = layoutMode.value === 'pip';

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let calloutId = 0;
    const notify = (text: string) => {
      const id = ++calloutId;
      setCallouts((c) => [...c.slice(-2), { id, text }]);
      setTimeout(() => setCallouts((c) => c.filter((x) => x.id !== id)), 1500);
    };
    const session = new MatchSession(start, myId, start.hostId === myId, net.value, canvas, {
      onHud: (s) => {
        // Only re-render when something visible changed, so buttons stay stable under the pointer.
        const sig = JSON.stringify(s);
        if (sig === lastHudSig.current) return;
        lastHudSig.current = sig;
        setHud(s);
      },
      onCallout: notify,
      onEnd: () => {
        /* the server's results message moves everyone to the results screen */
      },
    });
    // Dev builds expose the session for poking at the field from the console (boat listing, etc).
    if (import.meta.env.DEV) (window as unknown as { __cratered?: unknown }).__cratered = { session, damageBoat, sfx };
    sessionRef.current = session;
    // A controller (a DualSense, say) drives the menus, walking and aiming.
    const pad = new PadInput(session, rootRef.current ?? canvas, () => notify('Controller connected'));
    return () => {
      pad.dispose();
      session.dispose();
      sessionRef.current = null;
    };
  }, [start.seed, myId]);

  // Host: hand absent players to the bot.
  useEffect(() => {
    const s = sessionRef.current;
    const l = lobby.value;
    if (!s || !l || !s.isHost) return;
    for (const p of start.players) {
      const lp = l.players.find((x) => x.id === p.id);
      s.setAway(p.id, !lp || !lp.connected);
    }
  }, [lobby.value]);

  useEffect(() => {
    if (sessionRef.current) sessionRef.current.pip = pip;
  }, [pip]);

  const players = start.players;
  const nameFor = (id: string | null) => players.find((p) => p.id === id)?.name ?? (id === 'ai' ? 'Raiders' : '');
  const avatarFor = (id: string | null) => players.find((p) => p.id === id)?.avatarUrl;
  const active = hud?.activePlayerId ?? null;
  const myTeams = hud?.teams.filter((t) => !t.isEnemyAi) ?? [];
  const enemyTeams = hud?.teams.filter((t) => t.isEnemyAi) ?? [];
  const leftTeams = enemyTeams.length ? myTeams : myTeams.filter((_, i) => i % 2 === 0);
  const rightTeams = enemyTeams.length ? enemyTeams : myTeams.filter((_, i) => i % 2 === 1);
  const s = sessionRef;
  const inTurn = !!hud && TURN_PHASES.has(hud.phase);
  const myTeamIndex = players.find((p) => p.id === myId)?.team ?? 0;
  const look = lookFor(profile.value?.equipped ?? { body: 'body.lean', outfit: 'outfit.none', trail: 'trail.none' }, TEAM_COLORS[myTeamIndex % TEAM_COLORS.length]);
  // The grid is as tall as its rows (five with the catapult tile, five without); the clamp below
  // keeps every tile inside the battlefield even on a 1000 x 560 window.
  const invRows = Math.max(1, Math.ceil((hud?.menu.length ?? 0) / INV_COLS));
  const invW = INV_COLS * INV_TILE + (INV_COLS - 1) * INV_GAP + 2 * INV_EDGE;
  const invH = invRows * INV_TILE + (invRows - 1) * INV_GAP + 2 * INV_EDGE;

  return (
    <div class="match" ref={rootRef}>
      <canvas ref={canvasRef} tabIndex={0} aria-label="Battlefield" />
      {hud && (
        <div class="hud" aria-live="off">
          <div class="hud-top">
            {!pip ? (
              <div class="team-hp">
                {leftTeams.map((t) => (
                  <HpRow key={t.team} team={t} />
                ))}
                {hud.coins !== null && (
                  <span class="coin-chip num" title="Team coins">
                    <span class="coin" aria-hidden="true" /> {hud.coins}
                  </span>
                )}
              </div>
            ) : <span />}
            <div class="timer">
              <div class="timer-pill">
                {active && active !== 'ai' && <Avatar name={nameFor(active)} url={avatarFor(active)} />}
                <span class="who">
                  {hud.phase === 'ended' ? 'Match over' : hud.phase === 'buy' ? 'Buy phase' : hud.phase === 'between' ? 'Next up…' : hud.live ? 'Brawl' : hud.activeName}
                </span>
                <span class={`time num${hud.secondsLeft <= 5 && (inTurn || hud.live || hud.phase === 'buy') ? ' low' : ''}`}>
                  {hud.live ? clock(hud.secondsLeft) : inTurn || hud.phase === 'buy' ? hud.secondsLeft : '·'}
                </span>
              </div>
              {!pip && (
                <div class="wind">
                  {hud.wind !== 0 && (
                    <>
                      <span>{hud.wind < 0 ? '◀' : ''}</span>
                      <span class="wind-track">
                        <span style={hud.wind < 0 ? { right: '50%', width: `${Math.abs(hud.wind) * 50}%` } : { left: '50%', width: `${Math.abs(hud.wind) * 50}%` }} />
                      </span>
                      <span>{hud.wind > 0 ? '▶' : ''}</span>
                    </>
                  )}
                  <span class="num">
                    {hud.fight.bestOf > 1 ? `Round ${hud.fight.number} of ${hud.fight.bestOf} · ` : ''}{hud.live ? 'Live' : `Turn ${hud.round}`}
                    {hud.wave > 1 ? ` · Wave ${hud.wave}` : ''}
                  </span>
                  {hud.fight.bestOf > 1 && (
                    <span class="score" aria-label="Rounds won">
                      {hud.fight.wins.map((w) => (
                        <span key={w.team} class="score-team" style={{ color: TEAM_COLORS[w.team % TEAM_COLORS.length] }}>
                          {Array.from({ length: Math.ceil(hud.fight.bestOf / 2) }, (_, i) => (i < w.wins ? '●' : '○')).join('')}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              )}
            </div>
            {!pip ? (
              <div class="team-hp right">
                {rightTeams.map((t) => (
                  <HpRow key={t.team} team={t} right />
                ))}
              </div>
            ) : <span />}
          </div>

          <div class="hud-mid">
            {callouts.map((c) => (
              <div key={c.id} class="callout">
                {c.text}
              </div>
            ))}
          </div>

          {/* Territory War style action menu, floating over the active mole: every action as an icon. In a brawl it stays up for the whole live phase. */}
          {hud.myTurn && (hud.phase === 'select' || hud.live) && hud.anchor && !pip && (
            <div
              class="inventory"
              style={{
                left: `clamp(${invW / 2 + INV_MARGIN}px, ${hud.anchor.x}px, calc(100% - ${invW / 2 + INV_MARGIN}px))`,
                top: `clamp(${invH + INV_MARGIN}px, ${hud.anchor.y}px, calc(100% - ${INV_MARGIN + 8}px))`,
              }}
              role="menu"
              aria-label="Actions"
            >
              {hud.menu.map((m, i) => (
                <button
                  key={m.id}
                  class={`inv${m.id === 'skip' ? ' skip' : ''}${hud.live && m.id === hud.held ? ' held' : ''}`}
                  data-pad
                  role="menuitem"
                  disabled={!m.enabled}
                  aria-label={ACTIONS[m.id].name}
                  title={`${ACTIONS[m.id].name}${m.ammo !== Infinity ? ` ×${m.ammo}` : ''} — ${m.reason ?? ACTIONS[m.id].hint}`}
                  onClick={() => s.current?.chooseAction(m.id)}
                >
                  <ActionIcon id={m.id} />
                  {m.ammo !== Infinity && <span class="ammo num">{m.ammo}</span>}
                  {i < 9 && <span class="key num">{i + 1}</span>}
                  {/* Brawl: the held tile's cooldown drains away as a dark sweep. */}
                  {hud.live && m.id === hud.held && hud.cooldown > 0 && <span class="cool" style={{ height: `${hud.cooldown * 100}%` }} aria-hidden="true" />}
                </button>
              ))}
            </div>
          )}

          {/* Raft Wars style shop between rounds. */}
          {hud.phase === 'buy' && !pip && (
            <div class="shop-backdrop">
              <section class="shop" role="dialog" aria-label="Buy phase">
                <header class="shop-head">
                  <div>
                    <p class="eyebrow">
                      {hud.fight.bestOf > 1 ? `Round ${hud.fight.number} of ${hud.fight.bestOf}` : hud.wave > 1 ? `Wave ${hud.wave}` : 'Match start'} · buy phase
                    </p>
                    <h2>{hud.fight.number > 1 || hud.wave > 1 ? 'Restock' : 'Stock up'}</h2>
                  </div>
                  <div class="shop-head-right">
                    {amPlayer && hud.coins !== null && (
                      <span class="coins-row">
                        <span class="coin" aria-hidden="true" />
                        <span class="num coins-big">{hud.coins}</span>
                        <span class="muted">team coins</span>
                      </span>
                    )}
                    <span class={`shop-timer num${hud.secondsLeft <= 5 ? ' low' : ''}`}>{hud.secondsLeft}s</span>
                  </div>
                </header>
                {amPlayer && hud.coins !== null ? (
                  <>
                    <div class="shop-section">
                      <p class="eyebrow">Attacks</p>
                      <div class="shop-grid">
                        {hud.shop.filter((item) => !item.gear).map((item) => (
                          <ShopCard key={item.weapon} item={item} onBuy={(n) => s.current?.buy(item.weapon, n)} />
                        ))}
                      </div>
                    </div>
                    {/* Gear and the crew share a row, so the whole shop fits a 1000 x 560 window without scrolling. */}
                    <div class="shop-split">
                      <div class="shop-section gear">
                        <p class="eyebrow">Gear · takes your move, not your turn</p>
                        <div class="shop-grid">
                          {hud.shop.filter((item) => item.gear).map((item) => (
                            <ShopCard key={item.weapon} item={item} onBuy={(n) => s.current?.buy(item.weapon, n)} />
                          ))}
                        </div>
                      </div>
                      <div class="shop-section kit">
                        <p class="eyebrow">
                          Crew {hud.crew.size}/{hud.crew.max} · Kit
                        </p>
                        <div class="shop-grid">
                          {hud.recruits.map((r) => (
                            <div
                              class="shop-card"
                              key={r.kind}
                              title={r.kind === 'floatie'
                                ? 'Sits on a floatie beside your side. Cannot move; one solid hit tips it in.'
                                : 'Stands with the crew and plays like a standard mole.'}
                            >
                              <span class="shop-icon mole">
                                <MoleCanvas look={look} />
                              </span>
                              <span class="shop-card-name">{r.kind === 'floatie' ? 'Floatie mole' : 'Boat mate'}</span>
                              <span class="shop-card-row">
                                <span class="price num">{r.price}</span>
                                <button class="btn small" data-pad disabled={!r.enabled} title={r.reason ?? ''} onClick={() => s.current?.recruit(r.kind)}>
                                  Hire
                                </button>
                              </span>
                            </div>
                          ))}
                          {hud.kit.map((item) => (
                            <div class="shop-card" key={item.id} title={item.blurb}>
                              <span class="shop-icon">
                                <GhostIcon />
                              </span>
                              <span class="shop-card-name">{item.name}</span>
                              <span class="shop-card-row">
                                <span class="price num">{item.owned ? '' : item.price}</span>
                                <button class="btn small" data-pad disabled={!item.canBuy} title={item.owned ? 'Owned for the match' : ''} onClick={() => s.current?.buyItem(item.id)}>
                                  {item.owned ? 'Owned' : 'Buy'}
                                </button>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <footer class="shop-foot">
                      <div class="ready-list" aria-label="Who is ready">
                        {hud.readyTeams.map((t) => (
                          <span key={t.name} class={`ready-pill${t.ready ? ' on' : ''}`}>
                            {t.name}
                          </span>
                        ))}
                      </div>
                      <button class={`btn big${hud.ready ? '' : ' primary'}`} data-pad data-pad-ready disabled={hud.ready} onClick={() => s.current?.setReady()}>
                        {hud.ready ? 'Waiting for others…' : 'Ready'}
                      </button>
                    </footer>
                  </>
                ) : (
                  <p class="muted">Teams are shopping. The round starts when everyone is ready.</p>
                )}
              </section>
            </div>
          )}

          {!pip && (
            <div class="hud-bottom">
              <div class="hud-left">
                <div class="hud-hint">{hud.hint}</div>
                {hud.myTurn && hud.phase === 'select' && !hud.moved && hud.ownUnits.length > 1 && (
                  <div class="unit-chips" role="group" aria-label="Your moles">
                    {hud.ownUnits.map((u) => (
                      <button key={u.id} class="unit-chip" aria-pressed={u.active} onClick={() => s.current?.selectUnit(u.id)}>
                        <span>{u.name}</span>
                        <span class="muted num">{u.hp}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {hud.myTurn && hud.live ? (
                // Brawl: walk and jump buttons for touch; nothing to finish, so no Done.
                <div class="move-pad" role="group" aria-label="Move">
                  <div class="pad-row">
                    <PadButton label="◀" onPress={() => s.current?.move(-1)} onRelease={() => s.current?.move(0)} />
                    <PadButton label="⤒" onPress={() => s.current?.jump()} onRelease={() => {}} />
                    <PadButton label="▶" onPress={() => s.current?.move(1)} onRelease={() => s.current?.move(0)} />
                  </div>
                </div>
              ) : hud.myTurn && hud.phase === 'move' && hud.pulling ? (
                <span />
              ) : hud.myTurn && hud.phase === 'move' ? (
                <div class="move-pad" role="group" aria-label="Move">
                  <div class="stamina" aria-label="Stamina">
                    <span style={{ width: `${hud.stamina * 100}%` }} />
                  </div>
                  <div class="pad-row">
                    <PadButton label="◀" onPress={() => s.current?.move(-1)} onRelease={() => s.current?.move(0)} />
                    <PadButton label="⤒" onPress={() => s.current?.jump()} onRelease={() => {}} />
                    <PadButton label="▶" onPress={() => s.current?.move(1)} onRelease={() => s.current?.move(0)} />
                    <button class="btn primary" onClick={() => s.current?.stopMove()}>
                      Done
                    </button>
                  </div>
                </div>
              ) : hud.myTurn && hud.phase === 'aim' && hud.action ? (
                <div class="aim-bar">
                  <ActionIcon id={hud.action} />
                  <span>{ACTIONS[hud.action].name}</span>
                  <button class="btn ghost" onClick={() => s.current?.cancel()}>
                    Back
                  </button>
                </div>
              ) : <span />}

              <div class="hud-actions">
                {amPlayer && hud.phase !== 'ended' && confirmForfeit && (
                  <button key="keep" class="btn ghost" onClick={() => setConfirmForfeit(false)}>
                    Keep playing
                  </button>
                )}
                {amPlayer && hud.phase !== 'ended' && (
                  <button
                    key="forfeit"
                    class={confirmForfeit ? 'btn danger' : 'btn ghost'}
                    onClick={() => {
                      if (!confirmForfeit) {
                        setConfirmForfeit(true);
                        return;
                      }
                      setConfirmForfeit(false);
                      s.current?.forfeit();
                    }}
                  >
                    {confirmForfeit ? 'Forfeit for real' : 'Forfeit'}
                  </button>
                )}
                {hud.myTurn && inTurn && (
                  <button key="skip" class="btn" onClick={() => s.current?.skipTurn()}>
                    Skip turn
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {!amPlayer && !pip && <div class="spectating">Spectating</div>}
    </div>
  );
}

function ShopCard({ item, onBuy }: { item: HudState['shop'][number]; onBuy: (count: number) => void }) {
  const def = ACTIONS[item.weapon];
  return (
    <div class="shop-card" title={def.hint}>
      <span class="shop-icon">
        <ActionIcon id={item.weapon} />
      </span>
      <span class="shop-card-name">{def.name}</span>
      <span class="shop-card-row">
        <span class="price num">{item.price}</span>
        <span class="stepper" role="group" aria-label={def.name + ' count'}>
          <button disabled={!item.canSell} data-pad-alt onClick={() => onBuy(-1)} aria-label="Sell one">
            −
          </button>
          <span class="num">{item.owned}</span>
          <button disabled={!item.canBuy} data-pad onClick={() => onBuy(1)} aria-label="Buy one">
            +
          </button>
        </span>
      </span>
    </div>
  );
}

/** A brawl round's clock, m:ss. */
function clock(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

function PadButton({ label, onPress, onRelease }: { label: string; onPress: () => void; onRelease: () => void }) {
  return (
    <button
      class="pad-btn"
      onPointerDown={(e) => {
        (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
        onPress();
      }}
      onPointerUp={onRelease}
      onPointerCancel={onRelease}
      onPointerLeave={onRelease}
      aria-label={label}
    >
      {label}
    </button>
  );
}

function HpRow({ team, right }: { team: HudState['teams'][number]; right?: boolean }) {
  const color = team.isEnemyAi ? '#c0392b' : TEAM_COLORS[team.team % TEAM_COLORS.length];
  const pct = team.maxHp > 0 ? (team.hp / team.maxHp) * 100 : 0;
  const dot = <TeamDot team={team.team} enemy={team.isEnemyAi} />;
  const track = (
    <span class="hp-track">
      <span style={{ width: `${pct}%`, background: color }} />
    </span>
  );
  const label = (
    <span class="num">
      {team.alive} · {team.hp}
    </span>
  );
  return right ? (
    <div class="hp-row right">
      {label}
      {track}
      {dot}
    </div>
  ) : (
    <div class="hp-row">
      {dot}
      {track}
      {label}
    </div>
  );
}

export type { ActionId };

/** A faint dotted arc ending in a ring: the last-shot ghost. */
function GhostIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path d="M4 26 C 8 10, 18 6, 26 14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="1 5" opacity="0.7" />
      <circle cx="26" cy="15" r="4" fill="none" stroke="currentColor" stroke-width="2.5" />
    </svg>
  );
}
